import { useState, useEffect } from "react";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, CreditCard, User, Calendar, Clock, PoundSterling, Loader2, AlertCircle, Filter, RefreshCw } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useFirebase } from "@/contexts/FirebaseContext";
import { format } from "date-fns";
import { collection, query, where, getDocs, orderBy, doc, getDoc } from "firebase/firestore";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { paymentService } from "@/services/paymentService";

interface StudentPayment {
  id: string;
  studentId: string;
  studentName: string;
  studentEmail: string;
  courseId: string;
  courseName: string;
  amount: number;
  status: "paid" | "pending";
  paymentMethod: "card" | "cash";
  createdAt: Date;
  paymentDate?: Date;
  adminId?: string;
  adminName?: string;
}

interface Student {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

const InstructorStudentPayments = () => {
  const [payments, setPayments] = useState<StudentPayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [paymentMethodFilter, setPaymentMethodFilter] = useState<string>("all");
  const { currentUser, getFirestore } = useFirebase();
  const { toast } = useToast();
  const [isUpdating, setIsUpdating] = useState(false);

    const fetchPayments = async () => {
      try {
        setLoading(true);
        const db = getFirestore();
        if (!db || !currentUser) {
          console.error("Firestore or user not initialized:", { db: !!db, currentUser: !!currentUser });
          throw new Error("Firestore or user not initialized");
        }

        console.log("Current instructor ID:", currentUser.uid);

        // 1. Get all enrollments where this instructor is assigned
        const enrollmentsRef = collection(db, "enrollments");
        const enrollmentsQuery = query(
          enrollmentsRef,
          where("instructorId", "==", currentUser.uid)
        );
        const enrollmentsSnapshot = await getDocs(enrollmentsQuery);
        
        console.log("Enrollments found:", enrollmentsSnapshot.size);

        if (enrollmentsSnapshot.empty) {
          console.log("No enrollments found for this instructor");
          setPayments([]);
          setLoading(false);
          return;
        }

        // 2. Get all student IDs from the enrollments
        const studentIds = [...new Set(enrollmentsSnapshot.docs.map(doc => doc.data().userId))];
        console.log("Student IDs:", studentIds);

        // 3. Get all payments for these students from studentPayments, batching by 30
        const paymentsRef = collection(db, "studentPayments");
        let allPaymentsDocs: any[] = [];
        for (let i = 0; i < studentIds.length; i += 30) {
          const batchIds = studentIds.slice(i, i + 30);
          if (batchIds.length === 0) continue;
          const paymentsQuery = query(
            paymentsRef,
            where("studentId", "in", batchIds),
            orderBy("createdAt", "desc")
          );
          const paymentsSnapshot = await getDocs(paymentsQuery);
          allPaymentsDocs = allPaymentsDocs.concat(paymentsSnapshot.docs);
        }
        console.log("Payments found:", allPaymentsDocs.length);

        // 4. Get student details for each student
        const students: { [key: string]: Student } = {};
        for (const studentId of studentIds) {
          const studentRef = doc(db, "users", studentId);
          const studentDoc = await getDoc(studentRef);
          if (studentDoc.exists()) {
            const studentData = studentDoc.data();
            students[studentId] = {
              id: studentId,
              firstName: studentData.firstName,
              lastName: studentData.lastName,
              email: studentData.email
            };
          }
        }

        // 5. Map payments with student details
        const paymentsData = allPaymentsDocs.map(doc => {
          const paymentData = doc.data();
          const student = students[paymentData.studentId];
          
          return {
            id: doc.id,
            studentId: paymentData.studentId,
            studentName: student ? `${student.firstName} ${student.lastName}` : 'Unknown Student',
            studentEmail: student?.email || 'No email',
            courseId: paymentData.courseId || '',
            courseName: paymentData.courseName || 'Unknown Course',
            amount: paymentData.amount || 0,
            status: paymentData.status || 'pending',
            paymentMethod: paymentData.paymentMethod || 'card',
            createdAt: paymentData.createdAt?.toDate() || new Date(),
            paymentDate: paymentData.paymentDate?.toDate(),
            adminId: paymentData.adminId,
            adminName: paymentData.adminName,
            enrollmentId: paymentData.enrollmentId
          };
        }) as StudentPayment[];

        console.log("Final processed payments:", paymentsData);
        setPayments(paymentsData);
      } catch (error) {
        console.error("Error in fetchPayments:", error);
        toast({
          title: "Error",
          description: "Failed to load payments. Please try again.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

  useEffect(() => {
    if (currentUser) {
      console.log("Starting payment fetch for instructor:", currentUser.uid);
      fetchPayments();
    } else {
      console.log("No current user found, skipping payment fetch");
    }
  }, [currentUser, getFirestore, toast]);

  const filteredPayments = payments.filter(payment => {
    const matchesSearch = 
      payment.studentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.courseName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.studentEmail.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || payment.status === statusFilter;
    const matchesPaymentMethod = paymentMethodFilter === "all" || payment.paymentMethod === paymentMethodFilter;

    return matchesSearch && matchesStatus && matchesPaymentMethod;
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "paid":
        return <Badge variant="outline" className="bg-green-50 text-green-600 border-green-200">Paid</Badge>;
      case "pending":
        return <Badge variant="outline" className="bg-yellow-50 text-yellow-600 border-yellow-200">Pending</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Calculate payment statistics
  const totalAmount = filteredPayments.reduce((sum, payment) => sum + payment.amount, 0);
  const paidAmount = filteredPayments
    .filter(payment => payment.status === "paid")
    .reduce((sum, payment) => sum + payment.amount, 0);
  const pendingAmount = filteredPayments
    .filter(payment => payment.status === "pending")
    .reduce((sum, payment) => sum + payment.amount, 0);
  const totalPayments = filteredPayments.length;
  const paidPayments = filteredPayments.filter(payment => payment.status === "paid").length;
  const pendingPayments = filteredPayments.filter(payment => payment.status === "pending").length;

  const handleUpdatePayments = async () => {
    try {
      setIsUpdating(true);
      await paymentService.updateExistingPaymentsWithInstructorId();
      toast({
        title: "Success",
        description: "Payments updated successfully. Refreshing...",
      });
      // Refresh payments
      fetchPayments();
    } catch (error) {
      console.error("Error updating payments:", error);
      toast({
        title: "Error",
        description: "Failed to update payments. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout role="instructor">
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role="instructor">
      <div className="container mx-auto p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Student Payments</h1>
          <Button 
            onClick={handleUpdatePayments} 
            disabled={isUpdating}
            className="flex items-center gap-2"
          >
            {isUpdating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Updating...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4" />
                Update Payments
              </>
            )}
          </Button>
        </div>
      <div className="space-y-6">
        <div>
          <p className="text-gray-500 mt-2">View payment history for your assigned students</p>
        </div>

        {/* Payment Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Total Amount</p>
                  <p className="text-2xl font-bold">{formatCurrency(totalAmount)}</p>
                </div>
                <div className="bg-blue-100 p-3 rounded-full">
                  <PoundSterling className="h-6 w-6 text-blue-600" />
                </div>
              </div>
              <div className="mt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Paid</span>
                  <span className="font-medium text-green-600">{formatCurrency(paidAmount)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Pending</span>
                  <span className="font-medium text-yellow-600">{formatCurrency(pendingAmount)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Total Payments</p>
                  <p className="text-2xl font-bold">{totalPayments}</p>
                </div>
                <div className="bg-purple-100 p-3 rounded-full">
                  <CreditCard className="h-6 w-6 text-purple-600" />
                </div>
              </div>
              <div className="mt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Paid</span>
                  <span className="font-medium text-green-600">{paidPayments}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Pending</span>
                  <span className="font-medium text-yellow-600">{pendingPayments}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Payment Methods</p>
                  <p className="text-2xl font-bold">
                    {filteredPayments.filter(p => p.paymentMethod === "card").length} Card
                  </p>
                </div>
                <div className="bg-green-100 p-3 rounded-full">
                  <CreditCard className="h-6 w-6 text-green-600" />
                </div>
              </div>
              <div className="mt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Cash</span>
                  <span className="font-medium">{filteredPayments.filter(p => p.paymentMethod === "cash").length}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
            <Input
              type="search"
              placeholder="Search by student name, email, or course..."
              className="pl-8"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
              </SelectContent>
            </Select>
            <Select value={paymentMethodFilter} onValueChange={setPaymentMethodFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by method" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Methods</SelectItem>
                <SelectItem value="card">Card</SelectItem>
                <SelectItem value="cash">Cash</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Payment List */}
        <div className="grid gap-6">
          {filteredPayments.map((payment) => (
            <Card key={payment.id} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-semibold">{payment.studentName}</h3>
                    <p className="text-gray-500">{payment.studentEmail}</p>
                    <p className="text-gray-500 mt-1">{payment.courseName}</p>
                  </div>
                  {getStatusBadge(payment.status)}
                </div>

                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div className="flex items-center space-x-2">
                    <PoundSterling className="h-4 w-4 text-gray-400" />
                    <span className="font-medium">{formatCurrency(payment.amount)}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <span>{format(payment.createdAt, "PPP")}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CreditCard className="h-4 w-4 text-gray-400" />
                    <span className="capitalize">{payment.paymentMethod}</span>
                  </div>
                  {payment.adminName && (
                    <div className="flex items-center space-x-2">
                      <User className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-500">Processed by: {payment.adminName}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}

          {filteredPayments.length === 0 && (
            <div className="text-center py-12">
              <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900">No payments found</h3>
              <p className="text-gray-500 mt-2">
                {searchTerm || statusFilter !== "all" || paymentMethodFilter !== "all"
                  ? "No payments match your search criteria"
                  : "No payment records available for your students"}
              </p>
            </div>
          )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default InstructorStudentPayments; 